import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { UserDto, HostelDto, BookingDto } from '../services/api';

// ============================================
// NITC Role Constants (per NITC role matrix)
// ============================================
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  TENANT_ADMIN: 'TENANT_ADMIN',
  HOSTEL_ADMIN: 'HOSTEL_ADMIN',
  WARDEN: 'WARDEN',
  CARETAKER: 'CARETAKER',
  FACULTY: 'FACULTY',
  STUDENT: 'STUDENT',
  GUEST: 'GUEST',
  STAFF: 'STAFF', // Legacy
} as const;

// ============================================
// NITC Permission Groups (per NITC role matrix)
// ============================================
export const PERMISSIONS = {
  // Full admin access
  ADMIN_ROLES: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN],

  // Can approve/reject bookings - Only Admin and Warden
  CAN_APPROVE_BOOKINGS: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN, ROLES.WARDEN],

  // Can view hostel-specific bookings - Admin, Warden, Caretaker, Faculty
  CAN_VIEW_HOSTEL_BOOKINGS: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN, ROLES.WARDEN, ROLES.CARETAKER, ROLES.FACULTY],

  // Can assign rooms - Admin, Warden, Caretaker
  CAN_ASSIGN_ROOMS: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN, ROLES.WARDEN, ROLES.CARETAKER],

  // Can confirm check-in/out - Everyone (self-service for own bookings, staff for all)
  CAN_CHECK_IN_OUT: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN, ROLES.WARDEN, ROLES.CARETAKER],

  // Can update room status - Admin, Warden, Caretaker
  CAN_UPDATE_ROOM_STATUS: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN, ROLES.WARDEN, ROLES.CARETAKER],

  // Can generate reports - Admin, Warden, Caretaker
  CAN_GENERATE_REPORTS: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN, ROLES.WARDEN, ROLES.CARETAKER],

  // Can manage users - Admin only
  CAN_MANAGE_USERS: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN],

  // Can create bulk bookings - Admin and Faculty
  CAN_CREATE_BULK_BOOKINGS: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN, ROLES.FACULTY],

  // Can block rooms - Admin only
  CAN_BLOCK_ROOMS: [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.HOSTEL_ADMIN],
} as const;

// ============================================
// Impersonation Types
// ============================================
export type ImpersonationMode = 'view_only' | 'full_access';

export interface ImpersonationSession {
  id: string;
  adminUserId: string;
  adminUserName: string;
  targetUserId: string;
  targetUserName: string;
  targetUserEmail: string;
  targetUserRoles: string[];
  targetTenantId?: string;
  targetTenantName?: string;
  mode: ImpersonationMode;
  startedAt: string;
  expiresAt: string;
  isActive: boolean;
  remainingMinutes: number;
}

// ============================================
// Auth Store
// ============================================
interface AuthState {
  user: UserDto | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Impersonation state
  isImpersonating: boolean;
  impersonationMode: ImpersonationMode | null;
  impersonationSessionId: string | null;
  impersonationExpiresAt: string | null;
  originalUser: UserDto | null;
  originalAccessToken: string | null;
  originalRefreshToken: string | null;

  // Actions
  setUser: (user: UserDto | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (user: UserDto, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;

  // Impersonation actions
  startImpersonation: (
    session: ImpersonationSession,
    impersonatedUser: UserDto,
    accessToken: string,
    refreshToken: string
  ) => void;
  endImpersonation: () => void;
  isViewOnlyMode: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      // Restore tokens from localStorage on initialization
      accessToken: typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null,
      refreshToken: typeof window !== 'undefined' ? localStorage.getItem('refreshToken') : null,
      isAuthenticated: false,
      isLoading: false,

      // Impersonation state
      isImpersonating: false,
      impersonationMode: null,
      impersonationSessionId: null,
      impersonationExpiresAt: null,
      originalUser: null,
      originalAccessToken: null,
      originalRefreshToken: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setTokens: (accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        set({ accessToken, refreshToken });
      },

      login: (user, accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('impersonation_session_id');
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          // Clear impersonation state on logout
          isImpersonating: false,
          impersonationMode: null,
          impersonationSessionId: null,
          impersonationExpiresAt: null,
          originalUser: null,
          originalAccessToken: null,
          originalRefreshToken: null,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      hasRole: (role) => {
        const { user } = get();
        if (!user?.roles) return false;
        // Handle both string roles and role objects with code property
        return user.roles.some((r: any) => 
          typeof r === 'string' ? r === role : r?.code === role
        );
      },

      hasAnyRole: (roles) => {
        const { user } = get();
        if (!user?.roles) return false;
        // Handle both string roles and role objects with code property
        return roles.some(role => 
          user.roles.some((r: any) => 
            typeof r === 'string' ? r === role : r?.code === role
          )
        );
      },

      // Impersonation actions
      startImpersonation: (session, impersonatedUser, accessToken, refreshToken) => {
        const currentState = get();

        // Store original user and tokens
        set({
          originalUser: currentState.user,
          originalAccessToken: currentState.accessToken,
          originalRefreshToken: currentState.refreshToken,

          // Set impersonated user and tokens
          user: impersonatedUser,
          accessToken: accessToken,
          refreshToken: refreshToken,

          // Set impersonation state
          isImpersonating: true,
          impersonationMode: session.mode === 'view_only' ? 'view_only' : 'full_access',
          impersonationSessionId: session.id,
          impersonationExpiresAt: session.expiresAt,
        });

        // Update localStorage with impersonation tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('impersonation_session_id', session.id);
      },

      endImpersonation: () => {
        const currentState = get();

        if (!currentState.isImpersonating || !currentState.originalUser) {
          return;
        }

        // Restore original user and tokens
        const originalAccessToken = currentState.originalAccessToken;
        const originalRefreshToken = currentState.originalRefreshToken;

        set({
          user: currentState.originalUser,
          accessToken: originalAccessToken,
          refreshToken: originalRefreshToken,

          // Clear impersonation state
          isImpersonating: false,
          impersonationMode: null,
          impersonationSessionId: null,
          impersonationExpiresAt: null,
          originalUser: null,
          originalAccessToken: null,
          originalRefreshToken: null,
        });

        // Restore localStorage tokens
        if (originalAccessToken) {
          localStorage.setItem('accessToken', originalAccessToken);
        }
        if (originalRefreshToken) {
          localStorage.setItem('refreshToken', originalRefreshToken);
        }
        localStorage.removeItem('impersonation_session_id');
      },

      isViewOnlyMode: () => {
        const { isImpersonating, impersonationMode } = get();
        return isImpersonating && impersonationMode === 'view_only';
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // Also persist tokens to keep in sync
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        // Persist impersonation state
        isImpersonating: state.isImpersonating,
        impersonationMode: state.impersonationMode,
        impersonationSessionId: state.impersonationSessionId,
        impersonationExpiresAt: state.impersonationExpiresAt,
        originalUser: state.originalUser,
        originalAccessToken: state.originalAccessToken,
        originalRefreshToken: state.originalRefreshToken,
      }),
      onRehydrateStorage: () => (state) => {
        // When rehydrating, ensure tokens are also in localStorage for the API client
        if (state?.accessToken) {
          localStorage.setItem('accessToken', state.accessToken);
        }
        if (state?.refreshToken) {
          localStorage.setItem('refreshToken', state.refreshToken);
        }
      },
    }
  )
);

// ============================================
// UI Store
// ============================================
interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';
  notifications: Notification[];
  
  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'light',
      notifications: [],

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebarCollapsed: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      
      setTheme: (theme) => {
        set({ theme });
        // Apply theme to document
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
          root.classList.add(systemTheme);
        } else {
          root.classList.add(theme);
        }
      },
      
      addNotification: (notification) => {
        const id = Math.random().toString(36).substring(7);
        set((state) => ({
          notifications: [...state.notifications, { ...notification, id }],
        }));
        // Auto remove after duration
        if (notification.duration !== 0) {
          setTimeout(() => {
            get().removeNotification(id);
          }, notification.duration || 5000);
        }
      },
      
      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),
      
      clearNotifications: () => set({ notifications: [] }),
    }),
    {
      name: 'ui-storage',
      version: 2, // Bump version to reset persisted theme
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
      migrate: (persistedState: any, version: number) => {
        // Reset theme to light on version upgrade
        if (version < 2) {
          return { ...persistedState, theme: 'light' };
        }
        return persistedState;
      },
    }
  )
);

// ============================================
// Booking Store
// ============================================
interface BookingFilters {
  status?: string;
  hostelId?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

interface BookingState {
  bookings: BookingDto[];
  selectedBooking: BookingDto | null;
  filters: BookingFilters;
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  
  // Actions
  setBookings: (bookings: BookingDto[]) => void;
  setSelectedBooking: (booking: BookingDto | null) => void;
  setFilters: (filters: Partial<BookingFilters>) => void;
  resetFilters: () => void;
  setLoading: (isLoading: boolean) => void;
  setPagination: (totalCount: number, currentPage: number, pageSize: number) => void;
}

const defaultBookingFilters: BookingFilters = {
  status: undefined,
  hostelId: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  search: undefined,
};

export const useBookingStore = create<BookingState>((set) => ({
  bookings: [],
  selectedBooking: null,
  filters: defaultBookingFilters,
  isLoading: false,
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,

  setBookings: (bookings) => set({ bookings }),
  setSelectedBooking: (booking) => set({ selectedBooking: booking }),
  setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
  resetFilters: () => set({ filters: defaultBookingFilters }),
  setLoading: (isLoading) => set({ isLoading }),
  setPagination: (totalCount, currentPage, pageSize) => set({ totalCount, currentPage, pageSize }),
}));

// ============================================
// Hostel Store
// ============================================
interface HostelState {
  hostels: HostelDto[];
  selectedHostel: HostelDto | null;
  isLoading: boolean;
  
  // Actions
  setHostels: (hostels: HostelDto[]) => void;
  setSelectedHostel: (hostel: HostelDto | null) => void;
  setLoading: (isLoading: boolean) => void;
}

export const useHostelStore = create<HostelState>((set) => ({
  hostels: [],
  selectedHostel: null,
  isLoading: false,

  setHostels: (hostels) => set({ hostels }),
  setSelectedHostel: (hostel) => set({ selectedHostel: hostel }),
  setLoading: (isLoading) => set({ isLoading }),
}));

// ============================================
// Booking Form Store (for multi-step booking)
// ============================================
interface BookingFormState {
  step: number;
  hostelId: number | null;
  checkInDate: string | null;
  checkOutDate: string | null;
  numberOfGuests: number;
  roomSelections: { roomTypeId: number; quantity: number }[];
  guestDetails: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    idProofType?: string;
    idProofNumber?: string;
    organization?: string;
    purpose?: string;
  } | null;
  specialRequests: string;
  
  // Actions
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setHostel: (hostelId: number) => void;
  setDates: (checkIn: string, checkOut: string) => void;
  setNumberOfGuests: (count: number) => void;
  setRoomSelections: (selections: { roomTypeId: number; quantity: number }[]) => void;
  setGuestDetails: (details: BookingFormState['guestDetails']) => void;
  setSpecialRequests: (requests: string) => void;
  reset: () => void;
}

const initialBookingFormState = {
  step: 1,
  hostelId: null,
  checkInDate: null,
  checkOutDate: null,
  numberOfGuests: 1,
  roomSelections: [],
  guestDetails: null,
  specialRequests: '',
};

export const useBookingFormStore = create<BookingFormState>((set) => ({
  ...initialBookingFormState,

  setStep: (step) => set({ step }),
  nextStep: () => set((state) => ({ step: Math.min(state.step + 1, 5) })),
  prevStep: () => set((state) => ({ step: Math.max(state.step - 1, 1) })),
  setHostel: (hostelId) => set({ hostelId }),
  setDates: (checkInDate, checkOutDate) => set({ checkInDate, checkOutDate }),
  setNumberOfGuests: (numberOfGuests) => set({ numberOfGuests }),
  setRoomSelections: (roomSelections) => set({ roomSelections }),
  setGuestDetails: (guestDetails) => set({ guestDetails }),
  setSpecialRequests: (specialRequests) => set({ specialRequests }),
  reset: () => set(initialBookingFormState),
}));
